import React from 'react';
import { Link } from 'react-router-dom';
import { ISimCard } from '@/types/api';

interface SimCardItemProps {
  sim: ISimCard;
  onSelect?: (sim: ISimCard) => void;
}

const SimCardItem: React.FC<SimCardItemProps> = ({ sim, onSelect }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Enabled':
        return 'bg-green-100 text-green-800';
      case 'Disabled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:text-gray-200';
    }
  };

  const formatDataUsage = (volume?: number, totalVolume?: number) => {
    if (volume === undefined && totalVolume === undefined) return 'N/A';

    const formatBytes = (bytes?: number) => {
      if (bytes === undefined || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const usedStr = formatBytes(volume);
    const totalStr = formatBytes(totalVolume);
    const percentage = totalVolume && totalVolume > 0 ? ((volume || 0) / totalVolume * 100).toFixed(1) : 0;

    return (
      <div className="flex flex-col">
        <span className="font-medium text-sm">{usedStr}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">/ {totalStr}</span>
        {totalVolume && totalVolume > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div
              className={`h-1.5 rounded-full ${Number(percentage) > 90 ? 'bg-red-500' : Number(percentage) > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(Number(percentage), 100)}%` }}
            ></div>
          </div>
        )}
      </div>
    );
  };

  const formatCost = (amount?: number, currency?: string) => {
    if (amount === undefined) return 'N/A';
    return `${amount.toFixed(2)} ${currency || 'EUR'}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <tr className="hover:bg-gray-50 dark:bg-gray-700">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {sim.iccid}
          </div>
          {sim.imsi && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              IMSI: {truncateText(sim.imsi, 12)}
            </div>
          )}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {sim.label || (
            <span className="text-gray-400 dark:text-gray-500 italic">No label</span>
          )}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col space-y-1">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sim.status)}`}>
            {sim.status}
          </span>
          {sim.imei_lock && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
              IMEI Locked
            </span>
          )}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        {sim.ip_address || (
          <span className="text-gray-400 dark:text-gray-500">Not assigned</span>
        )}
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        {formatDataUsage(sim.volume, sim.total_volume)}
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        {formatCost(sim.cost_amount, sim.cost_currency)}
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        {formatDate(sim.expiry_date)}
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {sim.imei ? (
            <div className="flex flex-col">
              <span>{sim.imei}</span>
              {sim.msisdn && (
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  {sim.msisdn}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">Not available</span>
          )}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          <Link
            to={`/sim/${sim.iccid}`}
            className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
          >
            View Details
          </Link>
        </div>
      </td>
    </tr>
  );
};

export default SimCardItem;